/**
 * Admin Pricing Management Page
 *
 * Single source of truth for all pricing data displayed across the app.
 * Supports HTML in feature strings for rich formatting.
 */
import { useState, useEffect, useCallback } from 'react';
import { api, PricingConfig, PricingPlan, PricingCssClass } from '../../services/api';
import {
  Save, Loader2, AlertCircle, CheckCircle, ExternalLink, Eye,
  Plus, Trash2, GripVertical, ChevronDown, ChevronRight, X,
} from 'lucide-react';

const STRIPE_DASHBOARD_URL = 'https://dashboard.stripe.com/products';

// Color map for plan card accents
const COLOR_MAP: Record<string, string> = {
  lime: 'border-green-400 bg-green-400/10',
  cyan: 'border-cyan-400 bg-cyan-400/10',
  coral: 'border-orange-400 bg-orange-400/10',
  violet: 'border-purple-400 bg-purple-400/10',
};

export function Pricing() {
  const [pricing, setPricing] = useState<PricingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState('');
  const [updatedBy, setUpdatedBy] = useState('');
  const [expandedPlans, setExpandedPlans] = useState<Set<string>>(new Set());
  const [previewPlan, setPreviewPlan] = useState<string | null>(null);

  const loadPricing = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getAdminPricing();
      setPricing(data.pricing || { plans: {} });
      setUpdatedAt(data.updated_at || '');
      setUpdatedBy(data.updated_by || '');
      // Expand all plans by default
      if (data.pricing?.plans) {
        setExpandedPlans(new Set(Object.keys(data.pricing.plans)));
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load pricing config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPricing(); }, [loadPricing]);

  async function handleSave() {
    if (!pricing) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.updateAdminPricing(pricing);
      setSuccess('Pricing config saved successfully');
      setTimeout(() => setSuccess(null), 3000);
      loadPricing();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save pricing config');
    } finally {
      setSaving(false);
    }
  }

  function updatePlan(planKey: string, field: string, value: unknown) {
    if (!pricing) return;
    setPricing({
      ...pricing,
      plans: {
        ...pricing.plans,
        [planKey]: { ...pricing.plans[planKey], [field]: value },
      },
    });
  }

  function updatePlanLimit(planKey: string, field: string, value: unknown) {
    if (!pricing) return;
    const plan = pricing.plans[planKey];
    setPricing({
      ...pricing,
      plans: {
        ...pricing.plans,
        [planKey]: { ...plan, limits: { ...plan.limits, [field]: value } },
      },
    });
  }

  function updateFeature(planKey: string, featureType: 'features' | 'paused_features', index: number, value: string) {
    if (!pricing) return;
    const plan = pricing.plans[planKey];
    const list = [...(plan[featureType] || [])];
    list[index] = value;
    updatePlan(planKey, featureType, list);
  }

  function addFeature(planKey: string, featureType: 'features' | 'paused_features') {
    if (!pricing) return;
    const plan = pricing.plans[planKey];
    const list = [...(plan[featureType] || []), ''];
    updatePlan(planKey, featureType, list);
  }

  function removeFeature(planKey: string, featureType: 'features' | 'paused_features', index: number) {
    if (!pricing) return;
    const plan = pricing.plans[planKey];
    const list = (plan[featureType] || []).filter((_: string, i: number) => i !== index);
    updatePlan(planKey, featureType, list);
  }

  function updateFaq(index: number, field: 'question' | 'answer', value: string) {
    if (!pricing) return;
    const faqs = [...(pricing.faq || [])];
    faqs[index] = { ...faqs[index], [field]: value };
    setPricing({ ...pricing, faq: faqs });
  }

  function addFaq() {
    if (!pricing) return;
    setPricing({ ...pricing, faq: [...(pricing.faq || []), { question: '', answer: '' }] });
  }

  function removeFaq(index: number) {
    if (!pricing) return;
    setPricing({ ...pricing, faq: (pricing.faq || []).filter((_, i) => i !== index) });
  }

  function togglePlan(planKey: string) {
    setExpandedPlans(prev => {
      const next = new Set(prev);
      if (next.has(planKey)) next.delete(planKey);
      else next.add(planKey);
      return next;
    });
  }

  const sortedPlans = pricing ? Object.entries(pricing.plans).sort((a, b) => (a[1].sort_order || 0) - (b[1].sort_order || 0)) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-4xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Pricing Configuration</h1>
              {updatedAt && (
                <p className="text-sm text-gray-500 mt-1">
                  Last updated: {new Date(updatedAt).toLocaleString()} by {updatedBy}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => window.open(STRIPE_DASHBOARD_URL, '_blank')}
                className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 flex items-center gap-2 text-sm font-medium"
              >
                <ExternalLink className="w-4 h-4" />
                Open Stripe
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2 font-medium"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Saving...' : 'Save All'}
              </button>
            </div>
          </div>

          {/* Alerts */}
          {error && (
            <div className="flex items-center p-3 mb-4 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
              {error}
              <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
            </div>
          )}
          {success && (
            <div className="flex items-center p-3 mb-4 bg-green-50 text-green-700 rounded-lg text-sm">
              <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
              {success}
            </div>
          )}

          {/* Plan Cards */}
          <div className="space-y-4">
            {sortedPlans.map(([planKey, plan]) => (
              <PlanEditor
                key={planKey}
                planKey={planKey}
                plan={plan}
                expanded={expandedPlans.has(planKey)}
                onToggle={() => togglePlan(planKey)}
                onUpdate={(field, value) => updatePlan(planKey, field, value)}
                onUpdateLimit={(field, value) => updatePlanLimit(planKey, field, value)}
                onUpdateFeature={(type, idx, val) => updateFeature(planKey, type, idx, val)}
                onAddFeature={(type) => addFeature(planKey, type)}
                onRemoveFeature={(type, idx) => removeFeature(planKey, type, idx)}
                onPreview={() => setPreviewPlan(planKey)}
              />
            ))}
          </div>

          {/* FAQs */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">FAQs</h2>
              <button
                onClick={addFaq}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-1 text-sm"
              >
                <Plus className="w-3.5 h-3.5" /> Add FAQ
              </button>
            </div>
            <div className="space-y-3">
              {(pricing?.faq || []).map((faq, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 space-y-2">
                      <input
                        value={faq.question}
                        onChange={(e) => updateFaq(i, 'question', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium"
                        placeholder="Question"
                      />
                      <textarea
                        value={faq.answer}
                        onChange={(e) => updateFaq(i, 'answer', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        rows={3}
                        placeholder="Answer (HTML allowed)"
                      />
                    </div>
                    <button
                      onClick={() => removeFaq(i)}
                      className="p-1 text-red-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Note */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Footer Note</label>
            <input
              value={pricing?.footer_note || ''}
              onChange={(e) => pricing && setPricing({ ...pricing, footer_note: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          {/* Bottom save */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2 font-medium"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>
        </div>
      </div>

      {/* Right Panel: CSS Class Reference */}
      <CssClassPanel classes={pricing?.css_classes || []} />

      {/* Preview Lightbox */}
      {previewPlan && pricing?.plans[previewPlan] && (
        <PreviewLightbox
          plan={pricing.plans[previewPlan]}
          planKey={previewPlan}
          onClose={() => setPreviewPlan(null)}
        />
      )}
    </div>
  );
}


// ---- Plan Editor ----
interface PlanEditorProps {
  planKey: string;
  plan: PricingPlan;
  expanded: boolean;
  onToggle: () => void;
  onUpdate: (field: string, value: unknown) => void;
  onUpdateLimit: (field: string, value: unknown) => void;
  onUpdateFeature: (type: 'features' | 'paused_features', index: number, value: string) => void;
  onAddFeature: (type: 'features' | 'paused_features') => void;
  onRemoveFeature: (type: 'features' | 'paused_features', index: number) => void;
  onPreview: () => void;
}

function PlanEditor({ planKey, plan, expanded, onToggle, onUpdate, onUpdateLimit, onUpdateFeature, onAddFeature, onRemoveFeature, onPreview }: PlanEditorProps) {
  const colorClass = COLOR_MAP[plan.color] || 'border-gray-300 bg-gray-50';

  return (
    <div className={`border-2 rounded-lg overflow-hidden ${colorClass}`}>
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-white/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
          <span className="text-lg font-semibold text-gray-900">{plan.name}</span>
          <span className="text-sm text-gray-500">({planKey})</span>
          <span className="px-2 py-0.5 bg-gray-200 rounded text-xs font-mono">{plan.display_price}{plan.period_label}</span>
          {plan.badge && (
            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">{plan.badge}</span>
          )}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onPreview(); }}
          className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-1 text-sm"
        >
          <Eye className="w-3.5 h-3.5" /> Preview
        </button>
      </button>

      {expanded && (
        <div className="bg-white p-5 border-t border-gray-200 space-y-4">
          {/* Core fields */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <Field label="Name" value={plan.name} onChange={(v) => onUpdate('name', v)} />
            <Field label="Display Price" value={plan.display_price} onChange={(v) => onUpdate('display_price', v)} />
            <Field label="Period Label" value={plan.period_label} onChange={(v) => onUpdate('period_label', v)} placeholder="/month, /qtr, etc." />
            <Field label="Period" value={plan.period} onChange={(v) => onUpdate('period', v)} placeholder="month, quarter, forever" />
            <Field label="Price (cents)" value={String(plan.price_cents || 0)} onChange={(v) => onUpdate('price_cents', parseInt(v) || 0)} type="number" />
            <Field label="Sort Order" value={String(plan.sort_order || 0)} onChange={(v) => onUpdate('sort_order', parseInt(v) || 0)} type="number" />
          </div>

          {/* Display fields */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <Field label="Color" value={plan.color} onChange={(v) => onUpdate('color', v)} placeholder="lime, cyan, coral, violet" />
            <Field label="Mono Label" value={plan.mono_label} onChange={(v) => onUpdate('mono_label', v)} placeholder="// active" />
            <Field label="Badge" value={plan.badge || ''} onChange={(v) => onUpdate('badge', v)} placeholder="Most Popular" />
          </div>

          {/* Tagline */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Tagline</label>
            <input
              value={plan.tagline}
              onChange={(e) => onUpdate('tagline', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          {/* CTA */}
          <div className="grid grid-cols-3 gap-3">
            <Field label="CTA Label" value={plan.cta_label} onChange={(v) => onUpdate('cta_label', v)} />
            <Field label="CTA URL" value={plan.cta_url} onChange={(v) => onUpdate('cta_url', v)} />
            <Field label="CTA Style" value={plan.cta_style} onChange={(v) => onUpdate('cta_style', v)} placeholder="primary, outline, outline-coral" />
          </div>

          {/* Badge Note */}
          {plan.badge_note !== undefined && (
            <Field label="Badge Note" value={plan.badge_note || ''} onChange={(v) => onUpdate('badge_note', v)} placeholder="Small text under price" />
          )}

          {/* Stripe Price ID */}
          <Field label="Stripe Price ID" value={plan.stripe_price_id || ''} onChange={(v) => onUpdate('stripe_price_id', v)} placeholder="price_..." />

          {/* Limits */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Limits</h4>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Max Resumes" value={String(plan.limits?.max_resumes ?? '')} onChange={(v) => onUpdateLimit('max_resumes', v ? parseInt(v) : undefined)} type="number" />
              <Field label="Monthly Jobs" value={String(plan.limits?.monthly_jobs ?? '')} onChange={(v) => onUpdateLimit('monthly_jobs', v ? parseInt(v) : undefined)} type="number" />
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">AI Enabled</label>
                <select
                  value={plan.limits?.ai_enabled ? 'true' : 'false'}
                  onChange={(e) => onUpdateLimit('ai_enabled', e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
          </div>

          {/* Features */}
          <FeatureList
            label="Features"
            items={plan.features || []}
            onUpdate={(i, v) => onUpdateFeature('features', i, v)}
            onAdd={() => onAddFeature('features')}
            onRemove={(i) => onRemoveFeature('features', i)}
          />

          {/* Paused Features */}
          {(plan.paused_features?.length > 0 || planKey === 'hibernate') && (
            <FeatureList
              label="Paused Features"
              items={plan.paused_features || []}
              onUpdate={(i, v) => onUpdateFeature('paused_features', i, v)}
              onAdd={() => onAddFeature('paused_features')}
              onRemove={(i) => onRemoveFeature('paused_features', i)}
            />
          )}
        </div>
      )}
    </div>
  );
}


// ---- Reusable Field ----
function Field({ label, value, onChange, placeholder, type = 'text' }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        placeholder={placeholder}
      />
    </div>
  );
}


// ---- Feature List Editor ----
function FeatureList({ label, items, onUpdate, onAdd, onRemove }: {
  label: string;
  items: string[];
  onUpdate: (index: number, value: string) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-700">{label}</h4>
        <button
          onClick={onAdd}
          className="px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 flex items-center gap-1 text-xs"
        >
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
            <input
              value={item}
              onChange={(e) => onUpdate(i, e.target.value)}
              className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-mono"
              placeholder="Feature text (HTML allowed)"
            />
            <div
              className="text-xs text-gray-400 min-w-[120px] truncate"
              dangerouslySetInnerHTML={{ __html: item }}
            />
            <button onClick={() => onRemove(i)} className="p-1 text-red-400 hover:text-red-600">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


// ---- CSS Class Reference Panel ----
function CssClassPanel({ classes }: { classes: PricingCssClass[] }) {
  return (
    <div className="w-72 border-l border-gray-200 bg-gray-50 overflow-auto flex-shrink-0 hidden xl:block">
      <div className="p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">CSS Classes Reference</h3>
        <p className="text-xs text-gray-500 mb-4">
          Use these classes in feature HTML to style text on the pricing page and checkout modal.
        </p>
        <div className="space-y-3">
          {classes.map((cls, i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-3">
              <code className="text-xs font-mono text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                {cls.class}
              </code>
              <p className="text-xs text-gray-600 mt-1">{cls.description}</p>
              <div className="mt-2 p-2 bg-gray-900 rounded text-sm text-gray-300">
                <span dangerouslySetInnerHTML={{ __html: cls.example }} />
              </div>
              <p className="text-[10px] text-gray-400 mt-1 font-mono break-all">{cls.example}</p>
            </div>
          ))}
          {classes.length === 0 && (
            <p className="text-xs text-gray-400 italic">No CSS classes defined yet.</p>
          )}

          {/* Always show built-in HTML tags */}
          <div className="border-t border-gray-200 pt-3 mt-3">
            <h4 className="text-xs font-semibold text-gray-600 mb-2">Built-in HTML Tags</h4>
            <div className="space-y-2">
              <div className="bg-white rounded border border-gray-200 p-2">
                <code className="text-xs font-mono text-blue-600">&lt;strong&gt;</code>
                <span className="text-xs text-gray-500 ml-1">Bold text</span>
              </div>
              <div className="bg-white rounded border border-gray-200 p-2">
                <code className="text-xs font-mono text-blue-600">&lt;em&gt;</code>
                <span className="text-xs text-gray-500 ml-1">Italic text</span>
              </div>
              <div className="bg-white rounded border border-gray-200 p-2">
                <code className="text-xs font-mono text-blue-600">&lt;span class="..."&gt;</code>
                <span className="text-xs text-gray-500 ml-1">Styled inline</span>
              </div>
              <div className="bg-white rounded border border-gray-200 p-2">
                <code className="text-xs font-mono text-blue-600">&amp;amp; &amp;mdash;</code>
                <span className="text-xs text-gray-500 ml-1">HTML entities</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ---- Preview Lightbox ----
function PreviewLightbox({ plan, onClose }: { plan: PricingPlan; planKey: string; onClose: () => void }) {
  const colorAccent = plan.color === 'lime' ? '#c8ff00'
    : plan.color === 'cyan' ? '#00e0ff'
    : plan.color === 'coral' ? '#ff5c5c'
    : plan.color === 'violet' ? '#b18cff'
    : '#c8ff00';

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="max-w-md w-full mx-4 rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#161c2e' }}
      >
        {/* Close button */}
        <div className="flex justify-end p-3">
          <button onClick={onClose} className="text-gray-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Card content — mimics the landing page style */}
        <div className="px-8 pb-8">
          {plan.badge && (
            <div
              className="inline-block px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4"
              style={{ background: colorAccent, color: '#06080f' }}
            >
              {plan.badge}
            </div>
          )}

          <p className="text-xs uppercase tracking-widest mb-2 font-mono" style={{ color: colorAccent, opacity: 0.7 }}>
            {plan.mono_label}
          </p>
          <h3 className="text-2xl font-bold text-white mb-1">{plan.name}</h3>
          <div className="flex items-baseline gap-1 mb-1">
            <span className="text-4xl font-extrabold text-white">{plan.display_price}</span>
            <span className="text-gray-500 text-sm">{plan.period_label}</span>
          </div>
          {plan.badge_note && (
            <p className="text-xs mt-1 font-mono" style={{ color: colorAccent, opacity: 0.6 }}>
              {plan.badge_note}
            </p>
          )}

          <p className="text-gray-500 text-sm mt-4 mb-6">{plan.tagline}</p>

          {/* Features */}
          {plan.features?.length > 0 && (
            <ul className="space-y-3 mb-6">
              {plan.features.map((f, i) => (
                <li key={i} className="flex items-start gap-3">
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: colorAccent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-300 text-sm" dangerouslySetInnerHTML={{ __html: f }} />
                </li>
              ))}
            </ul>
          )}

          {/* Paused Features */}
          {plan.paused_features?.length > 0 && (
            <>
              <p className="text-gray-500 text-[11px] font-mono uppercase tracking-wider mb-3">Paused</p>
              <ul className="space-y-3 mb-6">
                {plan.paused_features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6" />
                    </svg>
                    <span className="text-gray-500 text-sm" dangerouslySetInnerHTML={{ __html: f }} />
                  </li>
                ))}
              </ul>
            </>
          )}

          {/* CTA */}
          <div
            className="w-full py-3 px-6 rounded-xl font-bold text-sm text-center"
            style={{
              background: plan.cta_style === 'primary' ? colorAccent : 'transparent',
              color: plan.cta_style === 'primary' ? '#06080f' : colorAccent,
              border: plan.cta_style !== 'primary' ? `1px solid ${colorAccent}40` : 'none',
            }}
          >
            {plan.cta_label}
          </div>
        </div>
      </div>
    </div>
  );
}
